import React, { useMemo, useState } from 'react';
import './BillsHistoricSummary.scss';
import { FaFileInvoiceDollar } from "react-icons/fa";
import { FaDollarSign, FaHashtag, FaTriangleExclamation, FaMoneyBillTransfer, FaListCheck } from 'react-icons/fa6';

interface BillType {
  amount: number;
  bill_name: string;
  bill_date: string;
  local_bill_category_name?: string | null;
  global_bill_category_name?: string | null;
  [key: string]: any;
}

interface BillsHistoricSummaryProps {
  bills: BillType[];
}

const MAX_VISIBLE = 3;

const BillsHistoricSummary: React.FC<BillsHistoricSummaryProps> = ({ bills }) => {
  const [showAll, setShowAll] = useState(false);

  // Memoizar el resumen
  const summary = useMemo(() => {
    const total_bills = bills.length;
    const total_amount = bills.reduce((acc, bill) => acc + (bill.amount || 0), 0);
    // Agrupar por categoría (global o local)
    const category_summary: Record<string, { count: number; amount: number }> = {};
    bills.forEach(bill => {
      const cat = bill.global_bill_category_name || bill.local_bill_category_name || 'Sin categoría';
      if (!category_summary[cat]) category_summary[cat] = { count: 0, amount: 0 };
      category_summary[cat].count += 1;
      category_summary[cat].amount += bill.amount || 0;
    });
    return { total_bills, total_amount, category_summary };
  }, [bills]);

  const { total_bills, total_amount, category_summary } = summary;
  const categories = Object.entries(category_summary || {});
  // Ordenar por monto descendente
  categories.sort((a, b) => b[1].amount - a[1].amount);
  const displayedCategories = showAll ? categories : categories.slice(0, MAX_VISIBLE);

  // Calcular el pago más alto
  const highestBill = bills.reduce((max, bill) => bill.amount > (max?.amount ?? 0) ? bill : max, bills[0]);
  // Calcular el promedio por pago
  const average = total_bills > 0 ? total_amount / total_bills : 0;

  if (bills.length === 0) return null;

  return (
    <div className="summary-dashboard-container">
      <div className="date-range-label">
        Resumen de pagos/gastos cargados
      </div>
      <div className="dashboard-row">
        {/* ──────────────── Columna 1: Ranking por categoría ──────────────── */}
        <div className="dashboard-card leaderboard-card leaderboard-col">
          <div className="card-accent-bar" />
          <div className="card-body leaderboard-body">
            <div className="card-title">
              <span><FaTriangleExclamation/> Ranking Cuentas</span>
              <span className="badge">Top por monto</span>
            </div>
            <ol className="leaderboard-list">
              {displayedCategories.length > 0 ? (
                displayedCategories.map(([cat, summary], idx) => (
                  <li key={cat} className="leaderboard-item">
                    <div className="leaderboard-avatar">
                      {cat.slice(0,2).toUpperCase()}
                    </div>
                    <div className="leaderboard-text">
                      <div className="leader-name">{cat}</div>
                      <div className="leader-subinfo">
                        <span className="leader-count">Pagos: {summary.count}</span>
                        <span className="leader-amount">
                          • ${summary.amount.toLocaleString('de-DE', { maximumFractionDigits: 0 })} total
                        </span>
                      </div>
                    </div>
                  </li>
                ))
              ) : (
                <li className="no-data">No hay datos</li>
              )}
            </ol>
            {categories.length > MAX_VISIBLE && (
              <div className="show-more-container">
                <button
                  className="mp-button light"
                  onClick={() => setShowAll((prev) => !prev)}
                >
                  {showAll ? 'Mostrar menos' : 'Mostrar más'}
                </button>
              </div>
            )}
          </div>
        </div>
        {/* ──────────────── Columna 2: Estadísticas generales ──────────────── */}
        <div className="small-stats-column">
          <div className="dashboard-card small-card stacked-card">
            <div className="card-accent-bar" />
            <div className="card-body">
              <div className="card-title sm-title">
                <FaHashtag/> Total de pagos
              </div>
              <div className="summary-value">
                {total_bills}
              </div>
              <div className="summary-subtext">
                Categorías: {categories.length}
              </div>
            </div>
          </div>
          <div className="dashboard-card small-card stacked-card">
            <div className="card-accent-bar" />
            <div className="card-body">
              <div className="card-title sm-title">
                <FaDollarSign/> Monto total
              </div>
              <div className="summary-value">
                ${total_amount.toLocaleString('de-DE', { maximumFractionDigits: 0 })}
              </div>
              <div className="summary-subtext">
                {/* Promedio se mueve a Resumen rápido */}
              </div>
            </div>
          </div>
        </div>
        {/* ──────────────── Columna 3: Resumen rápido ──────────────── */}
        <div className="dashboard-card today-card">
          <div className="card-accent-bar" />
          <div className="card-body today-body">
            <div className="card-title text-left" > <FaListCheck size={20}/> Resumen rápido</div>
            <div className="today-block">
              <div className="today-value"> <FaFileInvoiceDollar size={20}/> Promedio por pago</div>
              <div className="today-value-large">${average.toLocaleString('de-DE', { maximumFractionDigits: 0 })}</div>
            </div>
            <div className="today-block">
              <div className="today-value"> <FaMoneyBillTransfer size={20}/> Pago más alto</div>
              <div className="today-value-large">${highestBill.amount.toLocaleString('de-DE', { maximumFractionDigits: 0 })}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillsHistoricSummary;